import { useEffect } from 'react';
import './App.scss';
import { Router, Route, Redirect, Switch, RouteComponentProps } from 'react-router-dom';
import { LOCAL_STORAGE_KEY_SESSION_KEY } from './services/FetchService';
import AuthClient from './clients/AuthClient';
import { setSessionData, setSessionKey } from './utils';
import ReportsPage from './pages/reports-page';
import ConfigPage from './pages/config-page';
import { ReportsActions } from './redux/reports-actions';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from './redux/app-store';
import { LoginPage } from './pages/login-page';
import { useAuth } from './redux/auth/auth-selectors';
import { actionAuthInit } from './redux/auth/auth-actions';
import { RevolvingDot } from 'react-loader-spinner';
import { history } from './index';
import { Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ErrorSnackbar } from './components/error-snackbar';
import DashboardPage from './pages/dashboard-page';
import { ErrorPage } from './pages/error-page';

const useStyles = makeStyles((theme: Theme) => ({
  loader: {
    display: 'flex',
    height: '100%',
    alignItems: 'center',
    textAlign: 'center',
  },
  loaderIcon: {
    flex: 1,
    textAlign: 'center',
  },
}));

const isLoginCode = (value: string) => value.startsWith('lc_');

// The login code in an /sso-login/ link, if it carries one rather than a session key
const loginCodeInPath = (pathname: string) => {
  const token = pathname.match(/^\/sso-login\/([^/]+)/)?.[1];
  return token && isLoginCode(token) ? token : null;
};

// A session key from a legacy link is stored here, before App's auth init reads it. A login code is redeemed by App
// before its auth init instead, so no request goes out with an older stored key.
const SsoLogin = ({ match }: RouteComponentProps<{ token: string }>) => {
  const { token } = match.params;
  if (isLoginCode(token)) return null;

  localStorage.setItem(LOCAL_STORAGE_KEY_SESSION_KEY, token);
  return <Redirect to={`/`} />;
};

function App() {
  const dispatch = useDispatch();
  const userConfig = useSelector((state: AppState) => state.reports.userConfig);

  const auth = useAuth();
  const styles = useStyles();

  useEffect(() => {
    if (!!auth.sessionKey) {
      ReportsActions.actionLoadReportGroups()(dispatch);
      ReportsActions.actionLoadTenantConfigs()(dispatch);
      ReportsActions.actionLoadUserConfig()(dispatch);
      ReportsActions.actionLoadTenantSettings(auth.sessionKey)(dispatch);
    }
  }, [auth, dispatch]);

  useEffect(() => {
    const code = loginCodeInPath(window.location.pathname);
    if (!code) {
      dispatch(actionAuthInit());
      return;
    }
    // The session data is stored with the key, as the login form does, so the code signs in a browser that has
    // never signed in here. A code that can't be redeemed, e.g. spent by a reload, leaves the stored session as it
    // is. Replace, so the code doesn't stay in back-button history.
    AuthClient.redeemLoginCode(code)
      .then((sessionKey) => (sessionKey ? AuthClient.signInWithSessionKey(sessionKey) : null))
      .then((sessionData) => {
        if (sessionData?.session?.sessionKey) {
          setSessionKey(sessionData.session.sessionKey);
          // Only what the login form stores; api/signin also returns database connection details
          setSessionData({ user: sessionData.user, session: sessionData.session });
        }
        history.replace('/');
        dispatch(actionAuthInit());
      });
  }, [dispatch]);

  return (
    <div className='App'>
      <ErrorSnackbar />
      {!auth.isInitialized && (
        <div className={styles.loader}>
          <RevolvingDot
            wrapperClass={styles.loaderIcon}
            wrapperStyle={{ display: 'inline-block' }}
            color='#385690'
            height={100}
            width={100}
          />
        </div>
      )}
      <Router history={history}>
        <Route path={`/sso-login/:token`} component={SsoLogin} />

        {auth.isInitialized && !auth.sessionKey && (
          <Switch>
            <Route exact path='/login' component={LoginPage} />
            <Redirect to='/login' />
            <Route path='*' component={ErrorPage} />
          </Switch>
        )}

        {auth.isInitialized && auth.sessionKey && (
          <Switch>
            <Route exact path='/dashboard' component={DashboardPage} />
            <Route
              exact
              path='/reports/:reportGroupCode/:reportCode'
              component={ReportsPage}
            />

            {userConfig?.isReportManagementAllowed && (
              <Switch>
                <Route exact path='/config' component={ConfigPage} />
                <Route
                  exact
                  path='/config/:reportDefinitionCode'
                  component={ConfigPage}
                />
                <Route path='*' component={ErrorPage} />
              </Switch>
            )}

            <Route exact path='/' render={() => <Redirect to='/dashboard' />} />
            <Route path='*' component={ErrorPage} />
          </Switch>
        )}
      </Router>
    </div>
  );
}

export default App;
